import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CurrencyDollarIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  PlusIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  UserIcon,
  CreditCardIcon,
  BanknotesIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import adminAPI from '../utils/api';
import ConfirmationModal from '../components/ConfirmationModal';

const Orders = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showStatusConfirmModal, setShowStatusConfirmModal] = useState(false);
  const [statusChangeData, setStatusChangeData] = useState(null);

  // Initialize state from URL parameters
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [filters, setFilters] = useState({
    status: searchParams.get('status') || 'all',
    payment_method: searchParams.get('payment_method') || 'all',
    date_from: searchParams.get('date_from') || '',
    date_to: searchParams.get('date_to') || ''
  });

  const [services, setServices] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: parseInt(searchParams.get('page')) || 1,
    totalPages: 1,
    hasNext: false,
    hasPrevious: false,
    count: 0
  });

  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    type: 'warning'
  });

  // Status options
  const statusOptions = [
    { value: 'all', label: 'Tous les statuts' },
    { value: 'pending', label: 'En attente' },
    { value: 'paid', label: 'Payé' },
    { value: 'cancelled', label: 'Annulé' },
    { value: 'refunded', label: 'Remboursé' }
  ];

  // Payment method options
  const paymentMethodOptions = [
    { value: 'all', label: 'Tous les moyens' },
    { value: 'cash', label: 'Espèces' },
    { value: 'card', label: 'Carte bancaire' },
    { value: 'transfer', label: 'Virement' },
    { value: 'check', label: 'Chèque' }
  ];

  // Fetch orders
  const fetchOrders = async (page = 1) => {
    setLoading(true);
    try {
      const queryFilters = { ...filters, search: searchTerm, page };
      const response = await adminAPI.getOrders(queryFilters);
      setOrders(response.results || []);
      // Calculate total pages dynamically from API response
      let totalPages = 1;

      if (response.count > 0) {
        if (response.next || response.previous) {
          // Use current page results length as page size if available
          const currentPageSize = response.results ? response.results.length : 1;
          if (response.next) {
            // Not the last page, use current results length as page size
            totalPages = Math.ceil(response.count / currentPageSize);
          } else {
            // Last page, we are on the final page
            totalPages = page;
          }
        } else {
          // Only one page
          totalPages = 1;
        }
      }

      setPagination({
        currentPage: page,
        totalPages: totalPages,
        hasNext: !!response.next,
        hasPrevious: !!response.previous,
        count: response.count
      });
    } catch (error) {
      toast.error('Erreur lors du chargement des commandes');
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all active services for dropdowns (no pagination)
  const fetchDropdownData = async () => {
    try {
      const servicesData = await adminAPI.getAllActiveServiceTypes();
      setServices(Array.isArray(servicesData) ? servicesData : []);
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    }
  };

  useEffect(() => {
    fetchOrders(pagination.currentPage);
    fetchDropdownData();
  }, []);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (filters.status !== 'all') params.set('status', filters.status);
    if (filters.payment_method !== 'all') params.set('payment_method', filters.payment_method);
    if (filters.date_from) params.set('date_from', filters.date_from);
    if (filters.date_to) params.set('date_to', filters.date_to);
    if (pagination.currentPage > 1) params.set('page', pagination.currentPage);

    setSearchParams(params);
  }, [searchTerm, filters, pagination.currentPage, setSearchParams]);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  // Handle search
  const handleSearch = () => {
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    fetchOrders(1);
  };

  // Reset filters
  const resetFilters = () => {
    setSearchTerm('');
    setFilters({
      status: 'all',
      payment_method: 'all',
      date_from: '',
      date_to: ''
    });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    setTimeout(() => fetchOrders(1), 100);
  };

  // Handle pagination
  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
    fetchOrders(page);
  };

  // Handle delete order
  const handleDeleteOrder = (orderId, clientName) => {
    setConfirmationModal({
      isOpen: true,
      title: 'Supprimer la commande',
      message: `Êtes-vous sûr de vouloir supprimer la commande de ${clientName} ? Cette action est irréversible.`,
      type: 'danger',
      onConfirm: async () => {
        try {
          await adminAPI.deleteOrder(orderId);
          toast.success('Commande supprimée avec succès');
          fetchOrders(pagination.currentPage);
          setConfirmationModal({ ...confirmationModal, isOpen: false });
        } catch (error) {
          toast.error('Erreur lors de la suppression de la commande');
          console.error('Error deleting order:', error);
        }
      }
    });
  };

  // Handle status change
  // Handle status change - show confirmation first
  const handleStatusChange = (orderId, newStatus) => {
    const order = orders.find(o => o.id === orderId);
    if (!order) return;

    // If status is not changing, do nothing
    if (order.status === newStatus) return;

    // Show confirmation modal
    setStatusChangeData({
      orderId,
      order,
      oldStatus: order.status,
      newStatus
    });
    setShowStatusConfirmModal(true);
  };

  // Confirm status change
  const confirmStatusChange = async () => {
    if (!statusChangeData) return;

    try {
      await adminAPI.updateOrder(statusChangeData.orderId, { status: statusChangeData.newStatus });
      toast.success('Statut de la commande mis à jour');
      fetchOrders(pagination.currentPage);
      setShowStatusConfirmModal(false);
      setStatusChangeData(null);
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  // Cancel status change
  const cancelStatusChange = () => {
    setShowStatusConfirmModal(false);
    setStatusChangeData(null);
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: ClockIcon, label: 'En attente' },
      paid: { color: 'bg-green-100 text-green-800', icon: CheckCircleIcon, label: 'Payé' },
      cancelled: { color: 'bg-red-100 text-red-800', icon: XCircleIcon, label: 'Annulé' },
      refunded: { color: 'bg-gray-100 text-gray-800', icon: XCircleIcon, label: 'Remboursé' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  };

  // Get payment method icon
  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'cash':
        return <BanknotesIcon className="w-4 h-4" />;
      case 'card':
        return <CreditCardIcon className="w-4 h-4" />;
      default:
        return <CurrencyDollarIcon className="w-4 h-4" />;
    }
  };

  return (
    <div className="">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Commandes & Paiements</h1>
          <p className="text-gray-600">Gérez les commandes et paiements des clients</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Nouvelle commande
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex-1 flex gap-4 items-center">
            <div className="relative flex-1 max-w-md">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par client, service, référence..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="input-field pl-10"
              />
            </div>
            <button onClick={handleSearch} className="btn-primary">
              Rechercher
            </button>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`btn-secondary flex items-center ${showFilters ? 'bg-primary-50 text-primary-700' : ''}`}
            >
              <FunnelIcon className="h-4 w-4 mr-2" />
              Filtres
              {showFilters ? <ChevronUpIcon className="h-4 w-4 ml-1" /> : <ChevronDownIcon className="h-4 w-4 ml-1" />}
            </button>
            <button onClick={resetFilters} className="btn-secondary">
              <XMarkIcon className="h-4 w-4 mr-2" />
              Reset
            </button>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 pt-4 border-t border-gray-200">
            <div>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="input-field w-full"
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div>
              <select
                value={filters.payment_method}
                onChange={(e) => handleFilterChange('payment_method', e.target.value)}
                className="input-field w-full"
              >
                {paymentMethodOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            <div>
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
                className="input-field w-full"
                placeholder="Date début"
              />
            </div>
            <div>
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
                className="input-field w-full"
                placeholder="Date fin"
              />
            </div>
          </div>
        )}
      </div>

      {/* Orders Table */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Commandes ({pagination.count})
          </h3>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12">
            <CurrencyDollarIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune commande</h3>
            <p className="mt-1 text-sm text-gray-500">Commencez par créer une nouvelle commande.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Service
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Paiement
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {orders.map((order) => {
                  return (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <UserIcon className="h-8 w-8 text-gray-400 mr-3" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {order.client?.full_name || 'N/A'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {order.client?.email || 'N/A'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {order.service_type?.name || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {order.service_type?.type === 'pack' ? 'Pack' : 'Abonnement'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {order.amount} MAD
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900">
                          {getPaymentMethodIcon(order.payment_method)}
                          <span className="ml-2">
                            {paymentMethodOptions.find(opt => opt.value === order.payment_method)?.label || order.payment_method}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(order.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(order.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          {/* Status update dropdown */}
                          <select
                            value={order.status}
                            onChange={(e) => handleStatusChange(order.id, e.target.value)}
                            className="text-xs border border-gray-300 rounded px-2 py-1"
                            title="Changer le statut"
                          >
                            <option value="pending">En attente</option>
                            <option value="paid">Payé</option>
                            <option value="cancelled">Annulé</option>
                            <option value="refunded">Remboursé</option>
                          </select>

                          <button
                            onClick={() => handleDeleteOrder(
                              order.id, 
                              order.client?.full_name || 'Client'
                            )}
                            className="text-gray-400 hover:text-error-600 p-1"
                            title="Supprimer"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={!pagination.hasPrevious}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Précédent
              </button>
              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={!pagination.hasNext}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Suivant
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Page <span className="font-medium">{pagination.currentPage}</span> sur{' '}
                  <span className="font-medium">{pagination.totalPages}</span>
                  {pagination.count && (
                    <span className="ml-2 text-gray-500">
                      ({pagination.count} élément{pagination.count > 1 ? 's' : ''} au total)
                    </span>
                  )}
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={!pagination.hasPrevious}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Précédent
                  </button>
                  {(() => {
                    const { currentPage, totalPages } = pagination;
                    const pages = [];
                    const maxVisiblePages = 5;

                    if (totalPages <= maxVisiblePages) {
                      // Show all pages if total is small
                      for (let i = 1; i <= totalPages; i++) {
                        pages.push(i);
                      }
                    } else {
                      // Always show first page
                      pages.push(1);

                      if (currentPage > 3) {
                        pages.push('...');
                      }

                      // Show pages around current page
                      const start = Math.max(2, currentPage - 1);
                      const end = Math.min(totalPages - 1, currentPage + 1);

                      for (let i = start; i <= end; i++) {
                        if (i !== 1 && i !== totalPages) {
                          pages.push(i);
                        }
                      }

                      if (currentPage < totalPages - 2) {
                        pages.push('...');
                      }

                      // Always show last page
                      if (totalPages > 1) {
                        pages.push(totalPages);
                      }
                    }

                    return pages.map((page, index) => (
                      page === '...' ? (
                        <span
                          key={`ellipsis-${index}`}
                          className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                        >
                          ...
                        </span>
                      ) : (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            page === currentPage
                              ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      )
                    ));
                  })()}
                  <button
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={!pagination.hasNext}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Suivant
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create Order Modal */}
      {showCreateModal && (
        <CreateOrderModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchOrders(pagination.currentPage);
          }}
          services={services}
        />
      )}

      {/* Status Change Confirmation Modal */}
      {showStatusConfirmModal && statusChangeData && (
        <StatusConfirmationModal
          isOpen={showStatusConfirmModal}
          onClose={cancelStatusChange}
          onConfirm={confirmStatusChange}
          order={statusChangeData.order}
          oldStatus={statusChangeData.oldStatus}
          newStatus={statusChangeData.newStatus}
        />
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() => setConfirmationModal({ ...confirmationModal, isOpen: false })}
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.message}
        type={confirmationModal.type}
      />
    </div>
  );
};

// Create Order Modal Component
const CreateOrderModal = ({ isOpen, onClose, onSuccess, services }) => {
  const [loading, setLoading] = useState(false);
  const [checkingClient, setCheckingClient] = useState(false);
  const [clientServiceInfo, setClientServiceInfo] = useState(null);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [clients, setClients] = useState([]);
  const [searchingClients, setSearchingClients] = useState(false);
  const [formData, setFormData] = useState({
    client: '',
    service_type: '',
    payment_method: 'cash',
    status: 'paid',
    payment_reference: '',
    notes: ''
  });

  const paymentMethods = [
    { value: 'cash', label: 'Espèces' },
    { value: 'card', label: 'Carte bancaire' },
    { value: 'transfer', label: 'Virement' },
    { value: 'check', label: 'Chèque' }
  ];

  const statusOptions = [
    { value: 'pending', label: 'En attente' },
    { value: 'paid', label: 'Payé' },
    { value: 'cancelled', label: 'Annulé' }
  ];

  const selectedService = services.find(s => s.id === formData.service_type);

  // Search for clients dynamically
  const searchClients = async (searchTerm) => {
    if (!searchTerm.trim() || searchTerm.length < 2) {
      setClients([]);
      setShowClientDropdown(false);
      return;
    }

    try {
      setSearchingClients(true);
      const response = await adminAPI.getUsers({
        role: 'client',
        search: searchTerm.trim(),
        dropdown: 'true' // This disables pagination
      });

      // Handle both paginated and non-paginated responses
      const clientsData = response.results || response || [];
      setClients(Array.isArray(clientsData) ? clientsData : []);
      setShowClientDropdown(clientsData.length > 0);
    } catch (error) {
      console.error('Error searching clients:', error);
      setClients([]);
      setShowClientDropdown(false);
    } finally {
      setSearchingClients(false);
    }
  };

  const handleClientSearch = (value) => {
    setClientSearchTerm(value);
    if (!value) {
      setFormData(prev => ({ ...prev, client: '' }));
      setClientServiceInfo(null);
      setClients([]);
      setShowClientDropdown(false);
    } else {
      // Debounce the search
      const timeoutId = setTimeout(() => {
        searchClients(value);
      }, 300);

      // Store timeout ID for cleanup
      if (handleClientSearch.timeoutId) {
        clearTimeout(handleClientSearch.timeoutId);
      }
      handleClientSearch.timeoutId = timeoutId;
    }
  };

  const handleClientSelect = (client) => {
    setClientSearchTerm(client.full_name || `${client.first_name} ${client.last_name}`.trim());
    setFormData(prev => ({ ...prev, client: client.id }));
    setShowClientDropdown(false);
    setClients([]); // Clear the search results
    checkClientServices(client.id);
  };

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen]);

  // Check client's active services when client is selected
  const checkClientServices = async (clientId) => {
    if (!clientId) {
      setClientServiceInfo(null);
      return;
    }

    setCheckingClient(true);
    setClientServiceInfo(null); // Reset previous info immediately

    try {
      const response = await adminAPI.getClientServices({ client_id: clientId, status: 'active' });
      const activeServices = response.results || response || [];

      if (activeServices.length > 0) {
        const activeService = activeServices[0];
        const endDate = new Date(activeService.end_date);
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
        endDate.setHours(0, 0, 0, 0);
        const isExpired = endDate < today;

        setClientServiceInfo({
          hasActive: true,
          service: activeService,
          isExpired: isExpired,
          endDate: endDate.toLocaleDateString('fr-FR')
        });
      } else {
        setClientServiceInfo({ hasActive: false });
      }
    } catch (error) {
      console.error('Error checking client services:', error);
      setClientServiceInfo(null);
    } finally {
      setCheckingClient(false);
    }
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const orderData = {
        ...formData,
        amount: selectedService?.price || 0
      };

      await adminAPI.createOrder(orderData);
      toast.success('Commande créée avec succès');
      onSuccess();
    } catch (error) {
      console.log('Order creation error:', error);

      // Extract the most relevant error message
      let errorMessage = 'Erreur lors de la création de la commande';

      if (error.error) {
        // Backend error object with error field
        errorMessage = error.error;
      } else if (error.message) {
        // Standard Error object or other message
        errorMessage = error.message;
      }

      // Show the error message
      toast.error(errorMessage);
      console.error('Error creating order:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      client: '',
      service_type: '',
      payment_method: 'cash',
      status: 'paid',
      payment_reference: '',
      notes: ''
    });
    setClientServiceInfo(null);
    setCheckingClient(false);
    setClientSearchTerm('');
    setShowClientDropdown(false);
    setClients([]);
    setSearchingClients(false);

    // Clear any pending search timeout
    if (handleClientSearch.timeoutId) {
      clearTimeout(handleClientSearch.timeoutId);
      handleClientSearch.timeoutId = null;
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-4">Créer une nouvelle commande</h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">Client *</label>
            <div className="relative">
              <input
                type="text"
                value={clientSearchTerm}
                onChange={(e) => handleClientSearch(e.target.value)}
                onFocus={() => {
                  if (clientSearchTerm.length >= 2) {
                    setShowClientDropdown(true);
                  }
                }}
                onBlur={() => {
                  // Delay hiding dropdown to allow click on items
                  setTimeout(() => setShowClientDropdown(false), 200);
                }}
                placeholder="Rechercher un client par nom ou email (min. 2 caractères)..."
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              />

              {/* Dropdown */}
              {showClientDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {searchingClients ? (
                    <div className="px-3 py-2 text-center text-gray-500">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400 mx-auto mb-2"></div>
                      Recherche en cours...
                    </div>
                  ) : clients.length > 0 ? (
                    clients.map(client => (
                      <div
                        key={client.id}
                        onClick={() => handleClientSelect(client)}
                        className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                      >
                        <div className="font-medium text-gray-900">
                          {client.full_name || `${client.first_name} ${client.last_name}`.trim() || client.email}
                        </div>
                        <div className="text-sm text-gray-500">{client.email}</div>
                      </div>
                    ))
                  ) : clientSearchTerm.length >= 2 ? (
                    <div className="px-3 py-2 text-center text-gray-500">
                      Aucun client trouvé
                    </div>
                  ) : (
                    <div className="px-3 py-2 text-center text-gray-500">
                      Tapez au moins 2 caractères pour rechercher
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Client service status indicator */}
            {checkingClient && (
              <div className="mt-2 text-sm text-gray-500 flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400 mr-2"></div>
                Vérification des services du client...
              </div>
            )}

            {clientServiceInfo && (
              <div className="mt-2">
                {clientServiceInfo.hasActive ? (
                  <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                    <div className="flex items-start">
                      <InformationCircleIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-2" />
                      <div>
                        <p className="text-sm font-medium text-blue-800">
                          ℹ️ Information sur le service client
                        </p>
                        <p className="text-xs mt-1 text-blue-700">
                          Service: {clientServiceInfo.service.service_type?.name} -
                          Expire le: {clientServiceInfo.endDate}
                          {clientServiceInfo.isExpired
                            ? ' (Expiré - Peut acheter un nouveau service)'
                            : ' (Actif - Peut acheter des services supplémentaires)'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center">
                      <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                      <p className="text-sm font-medium text-green-800">
                        ✅ Aucun service actif - Nouvelle commande autorisée
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Service *</label>
            <select
              value={formData.service_type}
              onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
            >
              <option value="">Sélectionner un service</option>
              {services.map((service) => (
                <option key={service.id} value={service.id}>
                  {service.name} - {service.price} MAD ({service.type === 'pack' ? 'Pack' : 'Abonnement'})
                </option>
              ))}
            </select>
          </div>

          {selectedService && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                <strong>Prix:</strong> {selectedService.price} MAD
              </p>
              <p className="text-sm text-gray-600">
                <strong>Sessions:</strong> {selectedService.session_count}
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Méthode de paiement</label>
            <select
              value={formData.payment_method}
              onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              {paymentMethods.map((method) => (
                <option key={method.value} value={method.value}>
                  {method.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              {statusOptions.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Référence de paiement</label>
            <input
              type="text"
              value={formData.payment_reference}
              onChange={(e) => setFormData({ ...formData, payment_reference: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Numéro de transaction, chèque, etc."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              rows="3"
              placeholder="Notes additionnelles..."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={
                loading ||
                !formData.client ||
                !formData.service_type
              }
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading && (
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {loading ? 'Création...' : 'Créer la commande'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Status Confirmation Modal Component
const StatusConfirmationModal = ({ isOpen, onClose, onConfirm, order, oldStatus, newStatus }) => {
  const getStatusLabel = (status) => {
    const statusLabels = {
      pending: 'En attente',
      paid: 'Payé',
      cancelled: 'Annulé',
      refunded: 'Remboursé'
    };
    return statusLabels[status] || status;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Confirmer le changement de statut
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">
              Vous êtes sur le point de modifier le statut de cette commande :
            </p>

            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="text-sm">
                <div className="font-medium text-gray-900 mb-1">
                  Client: {order.client?.full_name}
                </div>
                <div className="text-gray-600 mb-1">
                  Service: {order.service_type?.name}
                </div>
                <div className="text-gray-600">
                  Montant: {order.amount} MAD
                </div>
              </div>
            </div>

            <div className="flex items-center justify-center space-x-4 py-4">
              <div className="text-center">
                <div className="text-sm text-gray-500 mb-1">Statut actuel</div>
                <span className="inline-flex px-3 py-1 text-sm font-medium rounded-full bg-gray-100 text-gray-800">
                  {getStatusLabel(oldStatus)}
                </span>
              </div>

              <div className="text-gray-400">
                →
              </div>

              <div className="text-center">
                <div className="text-sm text-gray-500 mb-1">Nouveau statut</div>
                <span className="inline-flex px-3 py-1 text-sm font-medium rounded-full bg-primary-100 text-primary-800">
                  {getStatusLabel(newStatus)}
                </span>
              </div>
            </div>
          </div>

          <p className="text-sm text-gray-600 mb-6">
            Êtes-vous sûr de vouloir effectuer ce changement ?
          </p>

          {/* Actions */}
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Annuler
            </button>
            <button
              onClick={onConfirm}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Confirmer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Orders;
